import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, SlidersHorizontal, Map as MapIcon, List, Search } from "lucide-react";
import api from "@/lib/api";
import { catMap, SUBCATEGORIES } from "@/lib/categories";
import { useLocation } from "@/context/LocationContext";
import { ListingCard } from "@/components/ListingCard";
import { MapView } from "@/components/MapView";
import { Mascot } from "@/components/Mascot";
import KerkoPunePage from "@/pages/KerkoPunePage";
import { useSeo, breadcrumb } from "@/lib/seo";

const SORTS = [
  { k: "newest", l: "Më të rejat" }, { k: "rating", l: "Më të vlerësuarat" },
  { k: "price_asc", l: "Çmimi ↑" }, { k: "price_desc", l: "Çmimi ↓" }, { k: "distance", l: "Më afër" },
];

export default function CategoryPage() {
  const { key } = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { loc } = useLocation();
  const cat = catMap[key];
  useSeo(cat && { title: `${cat.label} – shqiptarët në diasporë`, description: `${cat.label} shqiptare pranë teje në Evropë. Gjej adresa, kontakte dhe oferta në GjejShqip.`,
    data: { "@context": "https://schema.org", ...breadcrumb([["Kryefaqja", "/"], [cat.label, `/kategoria/${key}`]]) } }, [key, cat?.label]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [sort, setSort] = useState(loc.coords ? "distance" : "newest");
  const [sub, setSub] = useState("");
  const [q, setQ] = useState(sp.get("q") || "");

  useEffect(() => { if (loc.coords) setSort((s) => (s === "newest" ? "distance" : s)); }, [loc.coords]);

  useEffect(() => {
    if (key === "pune") return;
    setLoading(true);
    const params = new URLSearchParams({ category: key, sort });
    if (sub) params.set("subcategory", sub);
    if (q) params.set("q", q);
    if (loc.coords) { params.set("lat", loc.coords.lat); params.set("lng", loc.coords.lng); }
    api.get(`/listings?${params.toString()}`).then((r) => setItems(r.data.items)).catch(() => {}).finally(() => setLoading(false));
  }, [key, sort, sub, q, loc.coords]);

  if (key === "pune") return <KerkoPunePage />;
  if (!cat) return <div className="p-8 text-center text-slate-500">Kategoria nuk u gjet.</div>;
  const Icon = cat.icon;
  const points = items.filter((i) => i.lat).map((i) => ({ ...i, category: key }));

  return (
    <div className="gs-fade">
      <div className={`bg-gradient-to-br ${cat.grad} text-white px-4 pt-3 pb-5 rounded-b-3xl`}>
        <button data-testid="back-button" onClick={() => nav(-1)} className="p-1.5 -ml-1 mb-2 rounded-full bg-white/20"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex items-center gap-2">
          <Icon className="w-7 h-7" />
          <h1 className="text-2xl font-extrabold">{cat.label}</h1>
        </div>
        <div className="relative mt-3">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input data-testid="category-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Kërko në ${cat.label}...`} className="w-full bg-white text-slate-800 rounded-xl pl-10 pr-3 h-11 outline-none text-sm" />
        </div>
      </div>

      <div className="px-4 mt-3">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
          <button data-testid="subcat-all" onClick={() => setSub("")} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${!sub ? `${cat.solid} text-white border-transparent shadow-sm` : `${cat.light} ${cat.text} ${cat.border}`}`}>Të gjitha</button>
          {(SUBCATEGORIES[key] || []).map((s) => (
            <button key={s} data-testid={`subcat-${s}`} onClick={() => setSub(sub === s ? "" : s)} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${sub === s ? `${cat.solid} text-white border-transparent shadow-sm` : `${cat.light} ${cat.text} ${cat.border}`}`}>{s}</button>
          ))}
        </div>
        <div className="flex items-center justify-between mt-2">
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="text-sm bg-white border border-slate-200 rounded-xl px-3 py-2 font-medium text-slate-700 outline-none">
            {SORTS.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
          </select>
          <div className="flex bg-slate-100 rounded-xl p-0.5">
            <button data-testid="view-list" onClick={() => setView("list")} className={`p-2 rounded-lg ${view === "list" ? "bg-white shadow-sm" : ""}`}><List className="w-4 h-4 text-slate-600" /></button>
            <button data-testid="view-map" onClick={() => setView("map")} className={`p-2 rounded-lg ${view === "map" ? "bg-white shadow-sm" : ""}`}><MapIcon className="w-4 h-4 text-slate-600" /></button>
          </div>
        </div>
      </div>

      <div className="px-4 mt-3">
        {loading ? (
          <p className="text-center text-slate-400 py-10">Duke ngarkuar...</p>
        ) : view === "map" ? (
          <MapView points={points} height={420} />
        ) : items.length ? (
          <div className="grid grid-cols-2 gap-3">{items.map((l) => <ListingCard key={l.id} listing={l} />)}</div>
        ) : (
          <Mascot title="Nuk u gjet asnjë rezultat" text="Provo një qytet ose nënkategori tjetër." />
        )}
      </div>
    </div>
  );
}
